// src/AuthProvider.tsx
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import {
    isLoggedIn,
    getAccessToken,
    getIdToken,
    handleRedirectCallback,
    logout as doLogout,
    redirectToRitLogin,
    redirectToGoogleLogin,
} from "./auth";
import { decodeJwt } from "./jwt";

type ActivationStatus = "unknown" | "active" | "pending" | "disabled";

type AuthState = {
    isLoading: boolean;
    isAuthenticated: boolean;

    email: string | null;
    activated: boolean | null; // null = not logged in / unknown, false = not activated

    activationStatus: ActivationStatus;

    loginRit: () => Promise<void>;
    loginGoogle: () => Promise<void>;
    logout: () => void;
    getAccessToken: () => Promise<string | null>;
};

const AuthContext = createContext<AuthState | null>(null);

async function fetchActivationStatus(_accessToken: string): Promise<ActivationStatus> {
    // Replace with your real endpoint later if you want.
    return "active";
}

function parseClaimsFromIdToken(idToken: string | null): { email: string | null; activated: boolean | null } {
    if (!idToken) return { email: null, activated: null };

    try {
        const claims = decodeJwt(idToken) as Record<string, any>;

        const email =
            claims.email ??
            claims["cognito:username"] ??
            null;

        const raw = claims["custom:activated"];

        // Decide your default if missing:
        // - If you want missing => "not activated", keep false
        // - If you want missing => "activated", change default to true
        const activated =
            raw === "true" ? true :
                raw === "false" ? false :
                    false;

        return { email, activated };
    } catch (e) {
        console.warn("Failed to decode id token claims:", e);
        return { email: null, activated: null };
    }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    const [email, setEmail] = useState<string | null>(null);
    const [activated, setActivated] = useState<boolean | null>(null);

    const [activationStatus, setActivationStatus] = useState<ActivationStatus>("unknown");

    useEffect(() => {
        let cancelled = false;

        async function init() {
            console.log("[auth] init start");
            setIsLoading(true);

            try {
                // 1) If we’re on ?code=..., exchange it for tokens
                const handled = await handleRedirectCallback();


                // 2) Determine auth status from stored tokens
                const authed = isLoggedIn();

                console.debug("[auth] handled:", handled, "authed:", authed);

                if (cancelled) {
                    return;
                }

                setIsAuthenticated(authed);

                if (!authed) {
                    setEmail(null);
                    setActivated(null);
                    setActivationStatus("unknown");
                    return;
                }

                // 3) Read claims from ID token
                const idToken = getIdToken();
                const claims = parseClaimsFromIdToken(idToken);
                setEmail(claims.email);
                setActivated(claims.activated);

                // 4) Optional: call backend for activation status (stubbed)
                const at = await getAccessToken(); // refreshes if needed

                if (!cancelled && at) {
                    const status = await fetchActivationStatus(at);
                    if (!cancelled) setActivationStatus(status);
                } else if (!cancelled) {
                    setActivationStatus("unknown");
                }
            } catch (e) {
                console.error("AuthProvider init failed:", e);

                // If tokens are already in storage, stay logged in.
                const authed = isLoggedIn();

                if (!cancelled) {
                    setIsAuthenticated(authed);

                    if (authed) {
                        // try to still populate claims so UI shows email immediately
                        const idToken = getIdToken();
                        const claims = parseClaimsFromIdToken(idToken);
                        setEmail(claims.email);
                        setActivated(claims.activated);
                        setActivationStatus("unknown"); // or leave as-is
                    } else {
                        setEmail(null);
                        setActivated(null);
                        setActivationStatus("unknown");
                    }
                }
            } finally {
                console.log("[auth] init complete");
                setIsLoading(false);
            }
        }

        init();
        return () => {
            cancelled = true;
        };
    }, []);

    const value = useMemo<AuthState>(
        () => ({
            isLoading,
            isAuthenticated,
            email,
            activated,
            activationStatus,
            loginRit: async () => {
                await redirectToRitLogin();
            },
            loginGoogle: async () => {
                await redirectToGoogleLogin();
            },
            logout: () => doLogout(),
            getAccessToken: () => getAccessToken(),
        }),
        [isLoading, isAuthenticated, email, activated, activationStatus]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
    return ctx;
}

export function RequireAuth() {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) return null; // or spinner
    if (!isAuthenticated) return <Navigate to="/" replace />;
    return <Outlet />;
}
